// src/Pages/Landing/Business/BusinessLandingPage.jsx
//
// CHANGES FROM PREVIOUS VERSION:
//   - FACTUAL: direct Google posting is LIVE — corrected in 6 places
//     (capability card, how-it-works step 03, time comparison, free tier,
//      premium tier, FAQ schema)
//   - POSITIONING: signal-driven / zero-prompt framing in hero + capabilities
//   - NEW: SIGNAL_FLOW strip replaces the static hero right panel
//   - NEW: PositioningSection — three-way category comparison + compounding moat
//   - NEW: HOW_IT_WORKS step 04 (the learning loop)
//   - Blog generation corrected from "built" to live Premium feature

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import EnhancedSEO from '../../../Components/SEO/EnhancedSEO';
import {
  Box, Container, Grid, Typography, Button, Chip, Stack,
  Divider, useTheme, useMediaQuery,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  ArrowRightAlt as ArrowFlowIcon,
  StorefrontOutlined as ProfileIcon,
  Share as SocialIcon,
  Article as ContentIcon,
  RateReview as ReviewIcon,
  CheckCircle as CheckIcon,
  StorefrontOutlined as BusinessIcon,
  DataObject as TrainIcon,
  AutoAwesome as ContextIcon,
  TrendingUp as LearnIcon,
  WbSunny as WeatherIcon,
  QuestionAnswer as ChatIcon,
  DashboardCustomize as DashboardIcon,
  SmartToy as GenericAIIcon,
  Bolt as BoltIcon,
  HistoryToggleOff as HistoryIcon,
} from '@mui/icons-material';

// ─── Design tokens ─────────────────────────────────────────────────────────────

const BRAND = {
  navy:    '#0D2B6B',
  blue:    '#1976D2',
  lightBg: '#F3F9FF',
  slate:   '#455A7B',
  muted:   '#90A4AE',
  border:  '#E2E8F0',
};

// ─── Signal → outcome (the mechanism, made literal) ────────────────────────────

const SIGNAL_FLOW = [
  {
    Icon:      ReviewIcon,
    signal:    'A 3-star review lands',
    signalAt:  '9:02pm',
    outcome:   'Reply drafted, live on Google',
    outcomeAt: '9:06pm',
    tint:      '#A5D6A7',
  },
  {
    Icon:      WeatherIcon,
    signal:    '94° all week',
    signalAt:  'Monday',
    outcome:   'Post written, scheduled to FB, IG, LinkedIn',
    outcomeAt: 'Tue 8am',
    tint:      '#81D4FA',
  },
  {
    Icon:      ChatIcon,
    signal:    'Fourth person asks about parking',
    signalAt:  'this month',
    outcome:   'FAQ published, chatbot answers it 24/7',
    outcomeAt: 'Live',
    tint:      '#CE93D8',
  },
];

// ─── Capabilities ──────────────────────────────────────────────────────────────

const FOUR_CAPABILITIES = [
  {
    Icon:      ReviewIcon,
    title:     'Review Responses',
    headline:  'A review lands at 9pm. The reply is live before you check your phone.',
    body:      'Revfer drafts a response in your voice the moment a review comes in, then publishes it directly to your Google Business Profile once you approve. Nothing to copy, nothing to paste. Yelp and other platforms are next.',
    stat:      '< 5 min',
    statLabel: 'from review to drafted reply',
    color:     '#2E7D32',
    bg:        'rgba(46,125,50,0.07)',
    prominent: true,
    examples:  [
      'Responds to what the customer actually said',
      'Written in your voice — not generic AI language',
      'Publishes straight to Google on approval',
    ],
  },
  {
    Icon:      SocialIcon,
    title:     'Social Posts',
    headline:  'A heatwave, a holiday, a slow Tuesday — the post writes itself around it.',
    body:      'Revfer watches local events, weather, seasonal shifts and your own customer signals, then writes and schedules posts to Facebook, Instagram and LinkedIn. You approve from your phone.',
    stat:      '3 posts/week',
    statLabel: 'on Premium, 1/week free',
    color:     '#0277BD',
    bg:        'rgba(2,119,189,0.07)',
    prominent: true,
    examples:  [
      'Triggered by real signals, not a content calendar',
      'Built on your actual business context',
      'Tailored tone for each platform',
    ],
  },
  {
    Icon:      ProfileIcon,
    title:     'Smart Business Profile',
    headline:  'A live page that answers customer questions 24/7.',
    body:      'Your Revfer profile becomes a live information desk. Customers ask about your hours, services and prices — Revfer answers in your voice, instantly, around the clock.',
    stat:      '24/7',
    statLabel: 'customer questions answered',
    color:     '#1565C0',
    bg:        'rgba(21,101,192,0.07)',
    prominent: false,
    examples:  [
      'Chatbot trained on your business',
      'FAQs generated from real questions',
      'No extra setup',
    ],
  },
  {
    Icon:      ContentIcon,
    title:     'Content Generation',
    headline:  'Blog posts and FAQs written from your customer signals.',
    body:      'When customers ask the same question repeatedly, Revfer turns it into a published FAQ your chatbot answers from then on. When review themes cluster, it drafts a blog post addressing them. Blog generation is a Premium feature.',
    stat:      'Automatic',
    statLabel: 'from customer signals',
    color:     '#6A1B9A',
    bg:        'rgba(106,27,154,0.07)',
    prominent: false,
    examples:  [
      'FAQs from repeated questions',
      'Blog posts from review clusters',
      'All in your voice',
    ],
  },
];

// ─── Positioning: the three categories ─────────────────────────────────────────

const POSITIONING = [
  {
    Icon:     DashboardIcon,
    category: 'Review dashboards',
    verdict:  'Tools you operate',
    body:     'Built for multi-location teams with someone whose job is marketing. You get a dashboard, templates and a seat license. The work still waits for you to log in and do it.',
    points:   [
      'Priced per location, per seat',
      'Templates you fill in yourself',
      'Nothing happens until you open it',
    ],
    isRevfer: false,
  },
  {
    Icon:     GenericAIIcon,
    category: 'General-purpose AI',
    verdict:  'A blank box',
    body:     'Powerful, cheap, and it knows nothing about your business. Every session starts from zero: you write the prompt, paste the context, judge the output, then copy it somewhere else.',
    points:   [
      'You supply the prompt every time',
      'No memory of your business',
      'You still do the posting',
    ],
    isRevfer: false,
  },
  {
    Icon:     BoltIcon,
    category: 'Revfer',
    verdict:  'Work already done',
    body:     'Revfer sees the signal and starts without being asked. The reply is drafted, the post is scheduled, the question is answered. You read it and tap approve.',
    points:   [
      'Starts itself from real signals',
      'Knows your services and your voice',
      'Publishes on approval',
    ],
    isRevfer: true,
  },
];

const HOW_IT_WORKS = [
  {
    Icon:  BusinessIcon,
    step:  '01',
    title: 'Tell Revfer about your business',
    body:  'Add your services, hours and what makes you different. 5 minutes. This becomes the context for everything Revfer writes — posts, review responses, FAQ answers.',
    time:  '5 min',
  },
  {
    Icon:  TrainIcon,
    step:  '02',
    title: 'Revfer watches your signals and starts the work',
    body:  'A new review appears — Revfer drafts a response in your voice. A heatwave, a holiday, a cluster of questions about parking — Revfer writes the post or the FAQ around it. Nothing to prompt. It arrives already done.',
    time:  'Automatic',
  },
  {
    Icon:  ContextIcon,
    step:  '03',
    title: 'You approve. It posts.',
    body:  'Open the app, read the draft, tap approve. The review reply publishes directly to Google. The social post schedules to Facebook, Instagram and LinkedIn. That is the entire job.',
    time:  '30 sec',
  },
  {
    Icon:  LearnIcon,
    step:  '04',
    title: 'It learns from every edit you make',
    body:  'Every draft you reword, skip, or approve untouched feeds back in. Revfer builds an operating history specific to your business — what your customers respond to, how you actually sound. Month six sounds more like you than month one.',
    time:  'Ongoing',
  },
];

const TIME_COMPARISON = [
  { activity: 'Responding to reviews',       manual: '2–3 hrs/wk', revfer: 'Drafted and published on approval' },
  { activity: 'Writing social posts',         manual: '3–4 hrs/wk', revfer: '5 min/wk approving'               },
  { activity: 'Answering customer questions', manual: '4–6 hrs/wk', revfer: 'Automatic 24/7 via profile'       },
  { activity: 'Creating FAQs and blog posts', manual: '2–3 hrs/wk', revfer: 'Auto-generated from signals'      },
];

// ─── Hero signal strip ─────────────────────────────────────────────────────────

const HeroSignalStrip = () => (
  <Box sx={{ bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 4, p: { xs: 3, md: 3.5 }, border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}>
    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 2, fontSize: 10, fontWeight: 700, display: 'block', mb: 2.5 }}>
      NOBODY PROMPTED ANY OF THIS
    </Typography>

    {SIGNAL_FLOW.map(({ Icon, signal, signalAt, outcome, outcomeAt, tint }, i, arr) => (
      <Box
        key={signal}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 2,
          pb: i < arr.length - 1 ? 2.25 : 0,
          mb: i < arr.length - 1 ? 2.25 : 0,
          borderBottom: i < arr.length - 1 ? '1px dashed rgba(255,255,255,0.14)' : 'none',
        }}
      >
        <Box sx={{ width: 38, height: 38, borderRadius: 1.75, bgcolor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon sx={{ fontSize: 19, color: tint }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, lineHeight: 1.4, display: 'block' }}>
            {signal}
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.35)' }}>{` · ${signalAt}`}</Box>
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.92)', fontWeight: 600, fontSize: 13.5,
              lineHeight: 1.45, mt: 0.4,
              display: 'flex', alignItems: 'flex-start', gap: 0.6,
            }}
          >
            <ArrowFlowIcon sx={{ color: tint, fontSize: 17, mt: '-1px', flexShrink: 0 }} />
            {outcome}
          </Typography>
        </Box>

        <Box sx={{ flexShrink: 0, px: 1.25, py: 0.4, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.1)' }}>
          <Typography variant="caption" sx={{ color: tint, fontWeight: 700, fontSize: 10.5, whiteSpace: 'nowrap' }}>
            {outcomeAt}
          </Typography>
        </Box>
      </Box>
    ))}

    <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, lineHeight: 1.55 }}>
        Your entire job was tapping approve.
      </Typography>
    </Box>
  </Box>
);

// ─── Prominent capability card ─────────────────────────────────────────────────

const CapabilityCard = ({ cap }) => {
  const { Icon, title, headline, body, stat, statLabel, color, bg, examples } = cap;
  return (
    <Box sx={{
      bgcolor:   'white',
      borderRadius: 3,
      p:         { xs: 3, md: 4 },
      border:    `1px solid ${BRAND.border}`,
      height:    '100%',
      display:   'flex',
      flexDirection: 'column',
      transition: 'all 0.25s ease',
      '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 32px ${color}18`, borderColor: `${color}40` },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon sx={{ color, fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="overline" sx={{ color, letterSpacing: 1.5, fontSize: 10, fontWeight: 700 }}>
              {title}
            </Typography>
            <Chip
              label="✓ Live"
              size="small"
              sx={{ bgcolor: 'rgba(46,125,50,0.1)', color: '#2E7D32', fontWeight: 700, fontSize: 10, height: 20 }}
            />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.navy, lineHeight: 1.3, fontSize: { xs: 17, md: 19 } }}>
            {headline}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ color: BRAND.slate, lineHeight: 1.75, mb: 2.5, fontSize: 14, flex: 1 }}>
        {body}
      </Typography>

      <Box sx={{ mb: 2.5 }}>
        {examples.map(ex => (
          <Box key={ex} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
            <CheckIcon sx={{ color, fontSize: 16, mt: 0.2, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: BRAND.slate, fontSize: 13 }}>{ex}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{
        display:       'inline-flex',
        alignSelf:     'flex-start',
        alignItems:    'baseline',
        gap:           0.75,
        bgcolor:       bg,
        borderRadius:  2,
        px:            2,
        py:            1,
        border:        `1px solid ${color}20`
      }}>
        <Typography sx={{ fontWeight: 800, color, fontSize: 18, lineHeight: 1 }}>{stat}</Typography>
        <Typography variant="caption" sx={{ color, fontWeight: 600, opacity: 0.8 }}>{statLabel}</Typography>
      </Box>
    </Box>
  );
};

// ─── Supporting feature card ───────────────────────────────────────────────────

const SupportingCard = ({ cap }) => (
  <Box sx={{
    bgcolor:      'white',
    borderRadius: 2,
    p:            3,
    border:       `1px solid ${BRAND.border}`,
    transition:   'all 0.2s',
    height:       '100%',
    '&:hover':    { boxShadow: '0 4px 16px rgba(0,0,0,0.06)', borderColor: `${cap.color}40` }
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: cap.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <cap.Icon sx={{ color: cap.color, fontSize: 20 }} />
      </Box>
      <Box>
        <Typography variant="overline" sx={{ color: cap.color, letterSpacing: 1.5, fontSize: 10, fontWeight: 700, display: 'block' }}>
          {cap.title}
        </Typography>
        <Typography variant="subtitle2" fontWeight={700} color={BRAND.navy} sx={{ lineHeight: 1.3 }}>
          {cap.headline}
        </Typography>
      </Box>
    </Box>
    <Typography variant="body2" sx={{ color: BRAND.slate, lineHeight: 1.7, fontSize: 13, mb: 1.5 }}>
      {cap.body}
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {cap.examples.map(ex => (
        <Chip
          key={ex}
          label={ex}
          size="small"
          sx={{ bgcolor: cap.bg, color: cap.color, fontSize: 11, height: 22, border: `1px solid ${cap.color}20` }}
        />
      ))}
    </Box>
  </Box>
);

// ─── Positioning card ──────────────────────────────────────────────────────────

const PositioningCard = ({ item }) => {
  const { Icon, category, verdict, body, points, isRevfer } = item;

  return (
    <Box sx={{
      height:        '100%',
      display:       'flex',
      flexDirection: 'column',
      p:             { xs: 3, md: 3.5 },
      borderRadius:  3,
      bgcolor:       isRevfer ? BRAND.navy : 'white',
      border:        isRevfer ? `2px solid ${BRAND.blue}` : `1px solid ${BRAND.border}`,
      boxShadow:     isRevfer ? '0 12px 40px rgba(13,43,107,0.22)' : 'none',
      transition:    'all 0.25s ease',
      '&:hover':     isRevfer
        ? { transform: 'translateY(-4px)' }
        : { borderColor: '#CBD5E1' },
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2, mb: 2,
        bgcolor: isRevfer ? 'rgba(129,212,250,0.16)' : '#F1F5F9',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 22, color: isRevfer ? '#81D4FA' : BRAND.muted }} />
      </Box>

      <Typography
        variant="overline"
        sx={{
          letterSpacing: 1.5, fontSize: 10, fontWeight: 700, display: 'block', mb: 0.75,
          color: isRevfer ? 'rgba(255,255,255,0.55)' : BRAND.muted,
        }}
      >
        {category}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 800, lineHeight: 1.25, mb: 1.5, fontSize: { xs: 19, md: 21 },
          color: isRevfer ? '#81D4FA' : '#64748B',
        }}
      >
        {verdict}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          lineHeight: 1.75, fontSize: 13.5, mb: 2.5, flex: 1,
          color: isRevfer ? 'rgba(255,255,255,0.8)' : BRAND.slate,
        }}
      >
        {body}
      </Typography>

      <Box>
        {points.map(p => (
          <Box key={p} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1, '&:last-child': { mb: 0 } }}>
            {isRevfer
              ? <CheckIcon sx={{ color: '#81D4FA', fontSize: 15, mt: 0.25, flexShrink: 0 }} />
              : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#CBD5E1', mt: '7px', flexShrink: 0 }} />}
            <Typography
              variant="body2"
              sx={{ fontSize: 12.5, lineHeight: 1.5, color: isRevfer ? 'rgba(255,255,255,0.85)' : '#7B8FA8' }}
            >
              {p}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Positioning section ───────────────────────────────────────────────────────

const PositioningSection = () => (
  <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: BRAND.lightBg }}>
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
        <Chip
          label="Why this is different"
          size="small"
          sx={{ mb: 2, bgcolor: 'rgba(25,118,210,0.1)', color: BRAND.blue, fontWeight: 700, border: '1px solid rgba(25,118,210,0.2)' }}
        />
        <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2 }}>
          Every other AI tool is a blank box<br />waiting for a prompt.
        </Typography>
        <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 580, mx: 'auto', lineHeight: 1.75 }}>
          When the owner is also the marketing department, the thing that fails isn't
          the writing — it's finding the moment to sit down and start. Revfer removes
          that step entirely.
        </Typography>
      </Box>

      <Grid container spacing={3} alignItems="stretch" sx={{ mb: { xs: 4, md: 5 } }}>
        {POSITIONING.map(item => (
          <Grid key={item.category} item xs={12} md={4}>
            <PositioningCard item={item} />
          </Grid>
        ))}
      </Grid>

      {/* Compounding history — the defensibility claim */}
      <Box sx={{
        p:            { xs: 3, md: 4.5 },
        borderRadius: 3,
        bgcolor:      'white',
        border:       `1px solid ${BRAND.border}`,
      }}>
        <Grid container spacing={{ xs: 2.5, md: 4 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <HistoryIcon sx={{ color: BRAND.blue, fontSize: 22 }} />
              <Typography variant="overline" sx={{ color: BRAND.blue, letterSpacing: 1.8, fontSize: 10, fontWeight: 700 }}>
                AND IT COMPOUNDS
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: BRAND.navy, mb: 1.5, fontSize: { xs: '1.25rem', md: '1.5rem' }, lineHeight: 1.3 }}>
              Anyone can copy a feature.<br />
              Nobody can copy months of knowing your business.
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND.slate, lineHeight: 1.8, fontSize: 14 }}>
              Revfer keeps an operating history with your business specifically — which posts
              performed, which drafts you reworded, what your customers actually ask about.
              Every approval and every edit sharpens the next draft. Month six sounds more like
              you than month one, and that history belongs to your account.
            </Typography>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box sx={{ bgcolor: BRAND.lightBg, borderRadius: 2.5, p: 3, border: '1px solid #DBEAFE' }}>
              {[
                { k: 'Week 1',   v: 'Drafts from your business description' },
                { k: 'Month 2',  v: 'Learns which posts your customers engage with' },
                { k: 'Month 6',  v: 'Writes the way you would have written it' },
              ].map(({ k, v }, i, arr) => (
                <Box
                  key={k}
                  sx={{
                    pb: i < arr.length - 1 ? 1.75 : 0,
                    mb: i < arr.length - 1 ? 1.75 : 0,
                    borderBottom: i < arr.length - 1 ? '1px dashed #BBDEFB' : 'none',
                  }}
                >
                  <Typography variant="caption" sx={{ color: BRAND.blue, fontWeight: 800, fontSize: 11, letterSpacing: 0.5, display: 'block', mb: 0.4 }}>
                    {k}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BRAND.slate, fontSize: 13, lineHeight: 1.5 }}>
                    {v}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  </Box>
);

// ─── Time comparison table ─────────────────────────────────────────────────────

const TimeComparisonTable = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${BRAND.border}`, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
      <Box sx={{ bgcolor: BRAND.navy, px: 3, py: 2, display: 'flex' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: 1.5, flex: 1.4 }}>
          WHAT YOU DO TODAY
        </Typography>
        {!isMobile && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: 1.5, flex: 1, textAlign: 'center' }}>
            YOUR TIME
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: 1.5, flex: 1, textAlign: 'right' }}>
          WITH REVFER
        </Typography>
      </Box>

      {TIME_COMPARISON.map(({ activity, manual, revfer }, i) => (
        <Box key={activity} sx={{ display: 'flex', px: 3, py: 1.75, bgcolor: i % 2 === 0 ? 'white' : '#F8FAFC', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ flex: 1.4, fontWeight: 500, color: BRAND.slate, fontSize: 13 }}>
            {activity}
          </Typography>
          {!isMobile && (
            <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 600, color: '#90A4AE', fontSize: 13, textDecoration: 'line-through' }}>
              {manual}
            </Typography>
          )}
          <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', fontWeight: 700, color: BRAND.blue, fontSize: 13 }}>
            {revfer}
          </Typography>
        </Box>
      ))}

      <Box sx={{ display: 'flex', px: 3, py: 2, bgcolor: '#E8F5E9', alignItems: 'center', borderTop: '2px solid #C8E6C9' }}>
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 700, color: '#2E7D32', fontSize: 14 }}>
          Total time reclaimed
        </Typography>
        <Typography variant="h6" sx={{ flex: 1, textAlign: 'right', fontWeight: 800, color: '#2E7D32', fontSize: 18 }}>
          ~10 hrs/week
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const BusinessLandingPage = () => {
  const navigate       = useNavigate();
  const { authStatus } = useAuthenticator((c) => [c.authStatus]);

  const handleCTA = () => {
    if (authStatus === 'authenticated') {
      navigate('/company/new');
    } else {
      navigate('/auth/signin', { state: { name: 'business', link: '/landing/business' } });
    }
  };

  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "What does Revfer do?",
        "acceptedAnswer": { "@type": "Answer", "text": "Revfer watches a business's real signals — a new review landing, local weather and events, seasonal shifts, questions customers keep asking — and starts the work itself. Review replies are drafted in your voice and published directly to Google Business Profile on approval. Social posts are written and scheduled to Facebook, Instagram and LinkedIn. Recurring questions become published FAQs answered by a 24/7 chatbot. There is no prompt to write. The owner reads and approves." } },
      { "@type": "Question", "name": "Does Revfer post directly to Google reviews?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Approved replies publish directly to your Google Business Profile through a connected account — no copying or pasting. Yelp and additional platforms are next." } },
      { "@type": "Question", "name": "How is Revfer different from ChatGPT or a review dashboard?",
        "acceptedAnswer": { "@type": "Answer", "text": "General-purpose AI gives you an empty prompt box and knows nothing about your business. Review-management dashboards give you tools and templates you have to operate, priced for multi-location teams. Revfer initiates the work from signals it detects and hands you finished drafts to approve — roughly 10 minutes a week in place of about ten hours." } },
      { "@type": "Question", "name": "Does Revfer get better over time?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Revfer builds an operating history with each specific business — which content performed, which drafts the owner edited and how. Drafts sound more like the business each month. That history is specific to the business and does not transfer to another tool." } },
      { "@type": "Question", "name": "Who is Revfer for?",
        "acceptedAnswer": { "@type": "Answer", "text": "Businesses under about five employees — salons, restaurants, contractors, local services — where the owner is also the marketing department." } },
      { "@type": "Question", "name": "How much does Revfer cost?",
        "acceptedAnswer": { "@type": "Answer", "text": "Free tier available with 1 social post per week and unlimited review response drafts. Premium is $19/month at launch pricing, $49/month after launch, and includes direct publishing to Google. No credit card required to start." } },
      { "@type": "Question", "name": "How long does setup take?",
        "acceptedAnswer": { "@type": "Answer", "text": "About 15 minutes from signup to fully operational. No coding, no staff training." } },
    ],
  };

  return (
    <>
      <EnhancedSEO
        title="Revfer — AI that does the work, not waits for a prompt"
        description="Revfer watches your reviews, your season and your customers' questions, then drafts the reply, writes the post and answers the question — before you ask. Replies publish straight to Google on approval. For salons, restaurants, contractors. $19/mo launch pricing. Free to start."
        keywords="agentic AI small business, automatic review response Google Business Profile, AI review reply posting, social media autopilot small business, zero prompt AI marketing, review management alternative, salon marketing AI, restaurant review automation"
        type="website"
        image="/og-image.jpg"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* ═══ HERO ════════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        sx={{
          background: `linear-gradient(150deg, ${BRAND.navy} 0%, #1A3A7A 60%, #1565C0 100%)`,
          color: 'white',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Grid texture */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">

            {/* Left */}
            <Grid item xs={12} md={7}>
              <Chip
                label="For restaurants, salons and every business that runs on reputation"
                size="small"
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 700,
                  fontSize: 12,
                  border: '1px solid rgba(255,255,255,0.2)',
                  height: 'auto',
                  py: 0.75,
                  '& .MuiChip-label': { whiteSpace: 'normal' }
                }}
              />

              <Typography
                component="h1"
                variant="h1"
                sx={{ fontSize: { xs: '2rem', sm: '2.6rem', md: '3rem' }, fontWeight: 800, lineHeight: 1.15, mb: 3 }}
              >
                Your reviews get responses.<br />
                Your social media gets posted.<br />
                <Box component="span" sx={{ background: 'linear-gradient(90deg, #81D4FA, #A5F3FC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  You get your evenings back.
                </Box>
              </Typography>

              <Typography variant="h6" sx={{ opacity: 0.85, lineHeight: 1.7, mb: 4, fontWeight: 400, fontSize: { xs: '1rem', md: '1.1rem' }, maxWidth: 520 }}>
                Revfer watches what actually happens at your business — a review landing,
                a heatwave, the question customers keep asking — and starts the work itself.
                No prompt to write. You read what it drafted and tap approve.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 4 }}>
                <Button
                  variant="contained" size="large" endIcon={<ArrowIcon />} onClick={handleCTA}
                  sx={{ bgcolor: 'white', color: BRAND.blue, fontWeight: 700, fontSize: 16, px: 4, py: 2, borderRadius: 3, textTransform: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.92)', transform: 'translateY(-2px)' } }}
                >
                  {authStatus === 'authenticated' ? 'Set Up My Business' : 'Start Free — 15 min setup'}
                </Button>
                <Typography variant="body2" sx={{ opacity: 0.65, fontSize: 13, textAlign: { xs: 'center', sm: 'left' } }}>
                  No credit card · No coding · Cancel anytime
                </Typography>
              </Stack>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {[
                  { value: '13,000+', label: 'businesses signed up' },
                  { value: '$19/mo',  label: 'launch pricing'        },
                  { value: '~10 min', label: 'a week to run it'      },
                ].map(({ value, label }) => (
                  <Box key={label}>
                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1, mb: 0.25 }}>{value}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 500 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Right — signal → outcome strip */}
            <Grid item xs={12} md={5}>
              <HeroSignalStrip />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══ THE PROBLEM ═════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: '#FFFBF0', py: { xs: 6, md: 8 }, borderBottom: '1px solid #FFE082' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#5D4037', textAlign: 'center', mb: 1.5, fontSize: { xs: '1.3rem', md: '1.6rem' } }}>
            You didn't open a restaurant — or a salon, or a contracting business —
            to spend your nights writing Instagram captions.
          </Typography>
          <Typography variant="body1" sx={{ color: '#6D4C41', textAlign: 'center', maxWidth: 600, mx: 'auto', lineHeight: 1.7 }}>
            You're with customers all day. Then the books, the orders, the staff.
            Then — somewhere between 9pm and midnight — you remember you got a new review
            and still haven't posted this week.{' '}
            <Box component="strong">Something always slips.</Box>
          </Typography>
        </Container>
      </Box>

      {/* ═══ FOUR CAPABILITIES ═══════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Chip
              label="What Revfer does"
              size="small"
              sx={{ mb: 2, bgcolor: 'rgba(25,118,210,0.1)', color: BRAND.blue, fontWeight: 700, border: '1px solid rgba(25,118,210,0.2)' }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              Two features that run your evenings.<br />
              Two more that run your presence.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 560, mx: 'auto', lineHeight: 1.75 }}>
              Review responses and social posting are the core — live today, taking the most
              time off your plate. Smart business profile and content generation run quietly underneath.
            </Typography>
          </Box>

          {/* Top row — two prominent features */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {FOUR_CAPABILITIES.filter(c => c.prominent).map((cap) => (
              <Grid key={cap.title} item xs={12} md={6}>
                <CapabilityCard cap={cap} />
              </Grid>
            ))}
          </Grid>

          {/* Also included divider */}
          <Divider sx={{ mb: 4 }}>
            <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600, letterSpacing: 1.5, fontSize: 10 }}>
              ALSO INCLUDED
            </Typography>
          </Divider>

          {/* Bottom row — supporting features */}
          <Grid container spacing={3}>
            {FOUR_CAPABILITIES.filter(c => !c.prominent).map((cap) => (
              <Grid key={cap.title} item xs={12} md={6}>
                <SupportingCard cap={cap} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══ POSITIONING + MOAT ══════════════════════════════════════════════ */}
      <PositioningSection />

      {/* ═══ TIME COMPARISON ═════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
            <Chip
              label="What you get back"
              size="small"
              sx={{ mb: 2, bgcolor: 'rgba(46,125,50,0.08)', color: '#2E7D32', fontWeight: 700, border: '1px solid rgba(46,125,50,0.2)' }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              About 10 hours of your week, back.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 560, mx: 'auto', lineHeight: 1.75 }}>
              Most owner-operators spend 10–15 hours a week on reviews, social posts
              and customer questions — and still feel behind. Revfer compresses
              that to about 10 minutes of approvals.
            </Typography>
          </Box>
          <TimeComparisonTable />
        </Container>
      </Box>

      {/* ═══ HOW IT WORKS ════════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: BRAND.lightBg }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
            <Chip
              label="Setup"
              size="small"
              sx={{ mb: 2, bgcolor: 'rgba(46,125,50,0.1)', color: '#2E7D32', fontWeight: 700, border: '1px solid rgba(46,125,50,0.2)' }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 1.5, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              15 minutes to set up.<br />Then it runs — and improves.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 440, mx: 'auto', lineHeight: 1.7 }}>
              No coding. No staff training. Works alongside your existing accounts.
            </Typography>
          </Box>

          <Stack spacing={3}>
            {HOW_IT_WORKS.map(({ Icon, step, title, body, time }, i) => (
              <Box
                key={step}
                sx={{
                  display:        'flex',
                  gap:            { xs: 2.5, md: 4 },
                  alignItems:     'flex-start',
                  p:              { xs: 3, md: 4 },
                  bgcolor:        'white',
                  borderRadius:   3,
                  border:         `1px solid ${BRAND.border}`,
                  transition:     'all 0.2s',
                  '&:hover':      { boxShadow: '0 8px 24px rgba(0,0,0,0.06)', borderColor: '#BBDEFB' },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: BRAND.lightBg, border: '1px solid #BBDEFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ color: BRAND.blue, fontSize: 22 }} />
                  </Box>
                  {i < HOW_IT_WORKS.length - 1 && (
                    <Box sx={{ width: 2, height: 24, bgcolor: '#BBDEFB', borderRadius: 1 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="overline" sx={{ color: BRAND.muted, letterSpacing: 2, fontSize: 10, fontWeight: 700 }}>
                      STEP {step}
                    </Typography>
                    <Chip
                      label={`⏱ ${time}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(46,125,50,0.08)', color: '#2E7D32', fontWeight: 700, fontSize: 11, height: 22 }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.navy, mb: 0.75, fontSize: { xs: 16, md: 18 } }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BRAND.slate, lineHeight: 1.7, fontSize: 14 }}>
                    {body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ═══ PRICING ═════════════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 1.5, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              Start free. Upgrade when you're ready.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 440, mx: 'auto', lineHeight: 1.7 }}>
              Free tier lets you try both core features before committing.
              Premium is currently at launch pricing.
            </Typography>
          </Box>

          <Grid container spacing={3} alignItems="stretch">

            {/* Free */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 4, border: `1px solid ${BRAND.border}`, borderRadius: 3, height: '100%' }}>
                <Typography variant="overline" sx={{ color: BRAND.muted, letterSpacing: 2, fontSize: 11, fontWeight: 700, display: 'block', mb: 2 }}>
                  FREE
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, color: BRAND.navy, mb: 3 }}>
                  $0
                  <Typography component="span" variant="body1" sx={{ color: BRAND.muted, fontWeight: 500 }}>/mo</Typography>
                </Typography>
                {[
                  'Review response drafts — unlimited',
                  'Copy and post the draft yourself',
                  'Social media posts — 1 per week',
                  'Facebook, Instagram and LinkedIn',
                  'Smart business profile + 24/7 chatbot',
                  '1 business profile',
                ].map(f => (
                  <Box key={f} sx={{ display: 'flex', gap: 1.5, mb: 1.25, alignItems: 'flex-start' }}>
                    <CheckIcon sx={{ color: '#81C784', fontSize: 17, mt: 0.2, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: BRAND.slate, fontSize: 14 }}>{f}</Typography>
                  </Box>
                ))}
                <Button
                  fullWidth variant="outlined" size="large" onClick={handleCTA}
                  sx={{ mt: 3, borderColor: BRAND.border, color: BRAND.slate, borderRadius: 2.5, textTransform: 'none', fontWeight: 600 }}
                >
                  Start Free
                </Button>
              </Box>
            </Grid>

            {/* Premium */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 4, bgcolor: BRAND.navy, borderRadius: 3, height: '100%', position: 'relative', overflow: 'hidden' }}>
                <Chip
                  label="🚀 Launch pricing"
                  size="small"
                  sx={{ position: 'absolute', top: 20, right: 20, bgcolor: '#FF6F00', color: 'white', fontWeight: 700, fontSize: 11 }}
                />
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 2, fontSize: 11, fontWeight: 700, display: 'block', mb: 2 }}>
                  PREMIUM
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: 'white', lineHeight: 1 }}>
                    $19
                    <Typography component="span" variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>/mo</Typography>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through', fontWeight: 500 }}>
                    $49/mo
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#FFB74D', fontWeight: 700, display: 'block', mb: 3 }}>
                  Regular price $49/mo after launch
                </Typography>
                {[
                  'Review response drafts — unlimited',
                  'Replies publish directly to Google on approval',
                  'Yelp and more platforms next',
                  'Social media posts — 3 per week',
                  'Facebook, Instagram and LinkedIn',
                  'Smart business profile + 24/7 chatbot',
                  'Auto-FAQ from real customer questions',
                  'Blog post generation',
                  'Up to 5 business profiles',
                ].map(f => (
                  <Box key={f} sx={{ display: 'flex', gap: 1.5, mb: 1.25, alignItems: 'flex-start' }}>
                    <CheckIcon sx={{ color: '#81D4FA', fontSize: 17, mt: 0.2, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>{f}</Typography>
                  </Box>
                ))}
                <Button
                  fullWidth variant="contained" size="large" onClick={handleCTA} endIcon={<ArrowIcon />}
                  sx={{ mt: 3, bgcolor: BRAND.blue, color: 'white', borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontSize: 16, py: 1.75, '&:hover': { bgcolor: '#1565C0' } }}
                >
                  Start Free — Upgrade Anytime
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══ BOTTOM CTA ══════════════════════════════════════════════════════ */}
      <Box
        component="section"
        sx={{
          py:         { xs: 8, md: 10 },
          background: `linear-gradient(150deg, ${BRAND.navy} 0%, #1565C0 100%)`,
          color:      'white',
          textAlign:  'center',
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2 }}>
            Stop writing captions at 11pm.<br />
            <Box component="span" sx={{ color: '#81D4FA' }}>Start sleeping.</Box>
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.75, mb: 5, lineHeight: 1.7 }}>
            Revfer is for the restaurant owner, the salon owner, the contractor —
            anyone whose reputation is built one review and one post at a time.
          </Typography>
          <Button
            variant="contained" size="large" endIcon={<ArrowIcon />} onClick={handleCTA}
            sx={{ bgcolor: 'white', color: BRAND.blue, fontWeight: 700, fontSize: 17, px: 5, py: 2.25, borderRadius: 3, textTransform: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.92)', transform: 'translateY(-2px)' } }}
          >
            {authStatus === 'authenticated' ? 'Go to Dashboard' : 'Start Free — 15 min setup'}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.5 }}>
            No credit card required · Free tier · Cancel anytime
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default BusinessLandingPage;